import { ACTIVITY_TYPE_LABELS, type ActivityType, type ReportsOverview } from 'shared';
import { stepTitle } from '@/lib/reportPeriod';

type Cell = string | number;

// A semicolon, not a comma: it is what a spreadsheet set to Portuguese reads,
// and the comma is left free to be the decimal mark.
const SEPARATOR = ';';

const decimal = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 1, useGrouping: false });

// What a spreadsheet would run as a formula if a cell started with it.
const FORMULA_START = /^[=+\-@\t\r]/;

function cell(value: Cell): string {
  if (typeof value === 'number') return decimal.format(value);
  // A name is typed by someone: it must never be taken for a formula.
  const safe = FORMULA_START.test(value) ? `'${value}` : value;
  return /[";\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

function section(title: string, header: string[], rows: Cell[][]): string[] {
  return [cell(title), header.map(cell).join(SEPARATOR), ...rows.map((r) => r.map(cell).join(SEPARATOR)), ''];
}

export function activityTypeLabel(key: string): string {
  return ACTIVITY_TYPE_LABELS[key as ActivityType] ?? key;
}

export function universoLabel(key: string | null): string {
  return key ? key : 'Sem universo';
}

/** Every figure of a report, as one CSV file a spreadsheet opens. */
export function buildReportCsv(report: ReportsOverview, centreName: string): string {
  const { summary, previous } = report;

  const lines = [
    ...section(
      'Resumo',
      ['Indicador', 'Período', 'Período anterior'],
      [
        ['Centro', centreName, centreName],
        ['De', summary.from, previous.from],
        ['Até', summary.to, previous.to],
        ['Entradas', summary.visits, previous.visits],
        ['Entradas registadas pela equipa', summary.visitsByStaff, previous.visitsByStaff],
        ['Participantes diferentes', summary.uniqueParticipants, previous.uniqueParticipants],
        ['Visitantes em grupo', summary.visitors, previous.visitors],
        ['Novos registos', summary.newParticipants, previous.newParticipants],
        ['Sessões de atividades', summary.sessions, previous.sessions],
        ['Presenças em atividades', summary.attendance, previous.attendance],
        ['Horas de equipamento', summary.equipmentHours, previous.equipmentHours],
        ['Utilizações de equipamento', summary.equipmentSessions, previous.equipmentSessions],
        ['Credenciais atribuídas', summary.badgesAwarded, previous.badgesAwarded],
      ],
    ),
    ...section(
      'Evolução',
      ['Início', 'Período', 'Entradas', 'Visitantes em grupo', 'Presenças em atividades', 'Horas de equipamento'],
      report.series.map((p) => [
        p.start,
        stepTitle(p.start, report.bucket),
        p.visits,
        p.visitors,
        p.attendance,
        p.equipmentHours,
      ]),
    ),
    ...section(
      'Centros',
      ['Centro', 'Entradas', 'Presenças em atividades', 'Visitantes em grupo', 'Horas de equipamento'],
      report.byCenter.map((c) => [c.name, c.visits, c.attendance, c.visitors, c.equipmentHours]),
    ),
    ...section(
      'Atividades por tipo',
      ['Tipo', 'Atividades', 'Sessões', 'Presenças', 'Visitantes em grupo'],
      report.byType.map((t) => [activityTypeLabel(t.key), t.activities, t.sessions, t.attendance, t.visitors]),
    ),
    ...section(
      'Atividades por universo',
      ['Universo', 'Atividades', 'Sessões', 'Presenças', 'Visitantes em grupo'],
      report.byUniverso.map((u) => [universoLabel(u.key), u.activities, u.sessions, u.attendance, u.visitors]),
    ),
    ...section(
      'Atividades',
      ['Atividade', 'Tipo', 'Universo', 'Centro', 'Sessões', 'Presenças', 'Visitantes em grupo'],
      report.activities.map((a) => [
        a.name,
        activityTypeLabel(a.type),
        universoLabel(a.universo),
        a.digitalCenterName,
        a.sessions,
        a.attendance,
        a.visitors,
      ]),
    ),
    ...section(
      'Equipamento',
      ['Equipamento', 'Categoria', 'Centro', 'Utilizações', 'Participantes', 'Horas'],
      report.equipment.map((e) => [e.name, e.category, e.digitalCenterName, e.sessions, e.participants, e.hours]),
    ),
    ...section(
      'Credenciais',
      ['Credencial', 'Universo', 'Atribuídas'],
      report.badges.map((b) => [b.name, universoLabel(b.universo), b.awarded]),
    ),
    ...section(
      'Quem veio: idade',
      ['Faixa etária', 'Participantes'],
      [
        ['Até 14 anos', report.ageGroups.criancas],
        ['15 a 24 anos', report.ageGroups.jovens],
        ['25 a 64 anos', report.ageGroups.adultos],
        ['65 anos ou mais', report.ageGroups.seniores],
      ],
    ),
    ...section(
      'Quem veio: género',
      ['Género', 'Participantes'],
      [
        ['Feminino', report.gender.feminino],
        ['Masculino', report.gender.masculino],
        ['Indefinido', report.gender.indefinido],
      ],
    ),
  ];

  return lines.join('\r\n');
}

export function reportFileName(report: ReportsOverview): string {
  return `relatorio-digger-${report.summary.from}-a-${report.summary.to}.csv`;
}

/** Hands a CSV file to the browser to save. */
export function downloadCsv(fileName: string, content: string) {
  // The mark at the start tells a spreadsheet the accents are UTF-8.
  const blob = new Blob(['﻿', content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
